import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  notification,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoney } from '@/utils/format';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ENTITY_TYPE_EPC_ESTIMATE } from '@/constants/approval-entity-types';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { EpcPrepareProposalFromEstimateModal } from './EpcPrepareProposalFromEstimateModal';
import { openEpcEstimatePrint } from '@/utils/openEpcEstimatePrint';
import {
  Wave5ContingencyPanel,
  Wave5DurationPanel,
  Wave5EscalationPanel,
  Wave5FxPanel,
  Wave5IndirectsPanel,
  Wave5RisksPanel,
  useWave5Queries,
} from './EpcEstimateWave5Panels';
import {
  BidReconciliationPanel,
  ClientPricingPanel,
  ProfitabilityPanel,
  SensitivityPanel,
  StrategyPanel,
} from './EpcEstimateWave6Panels';
import { EpcEstimateEvidencePanel } from './EpcEstimateEvidencePanel';
import { EpcEstimateGovernancePanel } from './EpcEstimateGovernancePanel';
import { EpcEstimateGovernanceReadinessPanel } from './EpcEstimateGovernanceReadinessPanel';
import { EpcEstimateRevisionsWorkbench } from './EpcEstimateRevisionsWorkbench';
import { EpcBudgetHandoffPanel } from './EpcBudgetHandoffPanel';
import { EpcEstimateBoqLineWorkspace } from './EpcEstimateBoqLineWorkspace';
import type { BoqCoverageChipFilter, Est3Density } from './EpcEstimateBoqLineWorkspace';
import { EpcEstimateBoqLineDrawer } from './EpcEstimateBoqLineDrawer';
import { EpcEstimateCommandBar } from './EpcEstimateCommandBar';
import type { EpcEstimateCommandBarBcrNav } from './EpcEstimateCommandBar';
import { EpcEstimateSectionsDrawer } from './EpcEstimateSectionsDrawer';
import { EpcEstimateBoqCoverageStrip } from './EpcEstimateBoqCoverageStrip';
import { EpcEstimateCommercialHero } from './EpcEstimateCommercialHero';
import { EpcEstimateAttentionPanel } from './EpcEstimateAttentionPanel';
import { EpcEstimateIntelligenceRail } from './EpcEstimateIntelligenceRail';
import { EpcEstimateWorkflowStrip } from './EpcEstimateWorkflowStrip';
import { EpcEstimateCommercialBoqTable } from './EpcEstimateCommercialBoqTable';
import { EpcEstimateCostLineAddDrawer } from './EpcEstimateCostLineAddDrawer';
import { wbsLabelById, cbsLabelById } from '@/utils/est3WbsTree';
import { EpcEstimateWbsWorkbench } from './EpcEstimateWbsWorkbench';
import { EpcEstimateWbsImportWizard } from './EpcEstimateWbsImportWizard';
import { EpcEstimateCostLineDrawer } from './EpcEstimateCostLineDrawer';
import { EpcEstimateCostExplorerPanel } from './EpcEstimateCostExplorerPanel';
import { EpcEstimateDirectCostWorkbench } from './EpcEstimateDirectCostWorkbench';
import { EpcEstimateDiagnosticsWorkbench } from './EpcEstimateDiagnosticsWorkbench';
import { EpcEstimateEquipmentBuildUpPanel } from './EpcEstimateEquipmentBuildUpPanel';
import { EpcEstimateEquipmentWorkbench } from './EpcEstimateEquipmentWorkbench';
import { EpcEstimateLabourBuildUpPanel } from './EpcEstimateLabourBuildUpPanel';
import { EpcEstimateLabourWorkbench } from './EpcEstimateLabourWorkbench';
import { EpcEstimateMaterialBuildUpPanel } from './EpcEstimateMaterialBuildUpPanel';
import { EpcEstimateMaterialsWorkbench } from './EpcEstimateMaterialsWorkbench';
import { EpcEstimateCommercialLeaf } from './EpcEstimateCommercialLeaf';
import { EpcEstimateControlCenter } from './EpcEstimateControlCenter';
import { EpcEstimateProjectCostLeaf } from './EpcEstimateProjectCostLeaf';
import { EpcEstimateTimeRelatedWorkbench } from './EpcEstimateTimeRelatedWorkbench';
import type { Est3NavLeafKey } from './epcEstimateControlCenter.nav';
import { EST3_DEFAULT_LEAF } from './epcEstimateControlCenter.nav';
import { EpcEstimateOverviewHealth } from './EpcEstimateOverviewHealth';
import { EpcEstimateStatusStrip } from './EpcEstimateStatusStrip';
import { EpcEstimateSubcontractWorkbench } from './EpcEstimateSubcontractWorkbench';
import { EpcWbsLibraryPlugModal } from './EpcWbsLibraryPlugModal';
import type { HealthIssue } from './workbench/EpcEstimatingWorkbenchSurface';

const { Text } = Typography;

export interface EpcEstimateWorkspaceShellProps {
  tenderId: string;
  tenderTitle?: string;
  /** UX3.1 — Bid Control Room Estimate tab: collapse chrome, full-bleed workbench */
  focusMode?: boolean;
  /** UX3.2 — immersive BOQ-first layout (default true when focusMode) */
  immersiveBoq?: boolean;
  /** UX3.2 — merged BCR navigation in command bar */
  bcrNav?: EpcEstimateCommandBarBcrNav;
}

/**
 * EST3 operator shell — UX2 Control Center over Waves 1–8 panels.
 */
export const EpcEstimateWorkspaceShell: React.FC<EpcEstimateWorkspaceShellProps> = ({
  tenderId,
  tenderTitle,
  focusMode,
  immersiveBoq: immersiveBoqProp,
  bcrNav,
}) => {
  const immersive = !!(focusMode && (immersiveBoqProp ?? true));
  const qc = useQueryClient();
  const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(null);
  const [matLineId, setMatLineId] = useState<string | null>(null);
  const [labourLineId, setLabourLineId] = useState<string | null>(null);
  const [equipLineId, setEquipLineId] = useState<string | null>(null);
  const [selectedBoqLineId, setSelectedBoqLineId] = useState<string | null>(null);
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'unlinked'>('all');
  const [costDetailLine, setCostDetailLine] = useState<Record<string, unknown> | null>(null);
  const [costAddOpen, setCostAddOpen] = useState(false);
  const [costAddKindFilter, setCostAddKindFilter] = useState<
    'MATERIAL' | 'LABOUR' | 'EQUIPMENT' | undefined
  >();
  const [costAddRequireBoq, setCostAddRequireBoq] = useState(false);
  const [wbsImportOpen, setWbsImportOpen] = useState(false);
  const [intelligenceOpen, setIntelligenceOpen] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [commercialDrawer, setCommercialDrawer] = useState<
    'pricing' | 'indirects' | 'contingency' | 'duration' | null
  >(null);
  const [prepareProposalOpen, setPrepareProposalOpen] = useState(false);
  const [approvalDrawerOpen, setApprovalDrawerOpen] = useState(false);
  const [immersiveView, setImmersiveView] = useState<'boq' | Est3NavLeafKey>('boq');
  const [boqLineDrawerId, setBoqLineDrawerId] = useState<string | null>(null);
  const [boqImportOpen, setBoqImportOpen] = useState(false);
  const [boqAddOpen, setBoqAddOpen] = useState(false);
  const [boqChipFilter, setBoqChipFilter] = useState<BoqCoverageChipFilter>('all');
  const [controlLeaf, setControlLeaf] = useState<Est3NavLeafKey>(EST3_DEFAULT_LEAF);
  const [searchParams, setSearchParams] = useSearchParams();
  const [density] = useState<Est3Density>(() => {
    if (immersive) return 'compact';
    const v = localStorage.getItem('est3-density');
    return v === 'compact' || v === 'detail' ? v : 'standard';
  });
  const [boqForm] = Form.useForm();
  const [costForm] = Form.useForm();
  const [subPkgForm] = Form.useForm();
  const [crewCreateForm] = Form.useForm();
  const [prodCreateForm] = Form.useForm();
  const [labourRateForm] = Form.useForm();
  const [createEstimateOpen, setCreateEstimateOpen] = useState(false);
  const [createEstimateForm] = Form.useForm();

  const runtimeQ = useQuery({
    queryKey: ['epc-est-runtime'],
    queryFn: () => epcEstimationService.runtime(),
  });

  const listQ = useQuery({
    queryKey: ['epc-estimates', tenderId],
    queryFn: () => epcEstimationService.list(tenderId),
    enabled: !!tenderId && !runtimeQ.data?.disabled,
  });

  const estimateId = listQ.data?.[0]?.id as string | undefined;

  const wsQ = useQuery({
    queryKey: ['epc-estimate-ws', estimateId],
    queryFn: () => epcEstimationService.getWorkspace(estimateId!),
    enabled: !!estimateId,
  });

  const current =
    (selectedRevisionId &&
      wsQ.data?.revisions?.find((r: any) => r.id === selectedRevisionId)) ||
    wsQ.data?.currentRevision;

  const revisionId = current?.id as string | undefined;
  const readOnly = current && !['Draft', 'UnderReview', 'Rejected'].includes(String(current.status));

  const readinessQ = useQuery({
    queryKey: ['epc-est-readiness', estimateId],
    queryFn: () => epcEstimationService.readiness(estimateId!),
    enabled: !!estimateId,
  });

  const wbsQ = useQuery({
    queryKey: ['epc-est-wbs', estimateId, revisionId],
    queryFn: () => epcEstimationService.listWbs(estimateId!, revisionId!),
    enabled: !!estimateId && !!revisionId,
  });

  const cbsQ = useQuery({
    queryKey: ['epc-est-cbs', estimateId],
    queryFn: () => epcEstimationService.listCbs(estimateId!),
    enabled: !!estimateId,
  });

  const boqQ = useQuery({
    queryKey: ['epc-est-boq', estimateId, revisionId],
    queryFn: () => epcEstimationService.listClientBoq(estimateId!, revisionId!),
    enabled: !!estimateId && !!revisionId,
  });

  const costQ = useQuery({
    queryKey: ['epc-est-cost', estimateId, revisionId],
    queryFn: () => epcEstimationService.listCostLines(estimateId!, revisionId!),
    enabled: !!estimateId && !!revisionId,
  });

  const labourClassesQ = useQuery({
    queryKey: ['epc-est-lab-classes', estimateId],
    queryFn: () => epcEstimationService.listLabourClasses(estimateId!),
    enabled: !!estimateId,
  });

  const equipClassesQ = useQuery({
    queryKey: ['epc-est-eq-classes', estimateId],
    queryFn: () => epcEstimationService.listEquipmentClasses(estimateId!),
    enabled: !!estimateId,
  });

  const crewQ = useQuery({
    queryKey: ['epc-est-crews', estimateId],
    queryFn: () => epcEstimationService.listCrewTemplates(estimateId!),
    enabled: !!estimateId,
  });

  const prodQ = useQuery({
    queryKey: ['epc-est-prod', estimateId],
    queryFn: () => epcEstimationService.listProductivityNorms(estimateId!),
    enabled: !!estimateId,
  });

  const subPkgQ = useQuery({
    queryKey: ['epc-est-subpkg', estimateId, revisionId],
    queryFn: () => epcEstimationService.listSubcontractPackages(estimateId!, revisionId!),
    enabled: !!estimateId && !!revisionId,
  });

  const subCovQ = useQuery({
    queryKey: ['epc-est-subcov', estimateId, revisionId],
    queryFn: () => epcEstimationService.subcontractCoverage(estimateId!, revisionId!),
    enabled: !!estimateId && !!revisionId,
  });

  const boqExplorerQ = useQuery({
    queryKey: ['epc-est-explorer', estimateId, revisionId, 'client_boq'],
    queryFn: () => epcEstimationService.costExplorer(estimateId!, revisionId!, 'client_boq'),
    enabled: !!estimateId && !!revisionId,
  });

  const boqCoverageQ = useQuery({
    queryKey: ['epc-est-boq-coverage', estimateId, revisionId],
    queryFn: () => epcEstimationService.boqCostCoverage(estimateId!, revisionId!),
    enabled: !!estimateId && !!revisionId,
  });

  const diagQ = useQuery({
    queryKey: ['epc-est-diagnostics', estimateId],
    queryFn: () => epcEstimationService.diagnostics(estimateId!),
    enabled: !!estimateId,
  });

  const boqRollups = useMemo(() => {
    const m = new Map<
      string,
      {
        line_count: number;
        total_amount: number;
        sell_amount?: number;
        margin_pct?: number | null;
        coverage_status?: string;
      }
    >();
    for (const b of boqExplorerQ.data?.buckets || []) {
      if (b.key && b.key !== '_unallocated') {
        m.set(b.key, {
          line_count: b.line_count ?? 0,
          total_amount: b.total_amount ?? 0,
          sell_amount: b.sell_amount,
          margin_pct: b.margin_pct,
          coverage_status: b.coverage_status,
        });
      }
    }
    return m;
  }, [boqExplorerQ.data]);

  useEffect(() => {
    const fromUrl = searchParams.get('boqLineId');
    if (fromUrl) setSelectedBoqLineId(fromUrl);
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('importBoq') !== '1') return;
    setBoqImportOpen(true);
    setSearchParams(
      (p) => {
        p.delete('importBoq');
        return p;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const revFromUrl = searchParams.get('revision');
    if (revFromUrl) setSelectedRevisionId(revFromUrl);
    const leaf = searchParams.get('leaf');
    if (leaf === 'approval') {
      setApprovalDrawerOpen(true);
      setImmersiveView('approval');
      setControlLeaf('approval');
      setSearchParams(
        (p) => {
          p.delete('leaf');
          return p;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const selectBoqLine = (id: string | null) => {
    setSelectedBoqLineId(id);
    if (id) {
      setSearchParams((p) => {
        p.set('boqLineId', id);
        return p;
      });
    } else {
      setSearchParams((p) => {
        p.delete('boqLineId');
        return p;
      });
    }
  };

  const diagFindings = Array.isArray(diagQ.data?.findings)
    ? diagQ.data.findings
    : Array.isArray(diagQ.data)
      ? diagQ.data
      : [];

  const blockerCount = useMemo(() => {
    const blockers = (readinessQ.data as { blockers?: unknown[] })?.blockers;
    const bn = Array.isArray(blockers) ? blockers.length : 0;
    const dn = diagFindings.filter((d) => d.severity === 'error').length;
    return bn + dn;
  }, [readinessQ.data, diagFindings]);

  const openCostAdd = (
    opts?: { kind?: 'MATERIAL' | 'LABOUR' | 'EQUIPMENT'; boqLineId?: string; requireBoq?: boolean }
  ) => {
    setCostAddKindFilter(opts?.kind);
    setCostAddRequireBoq(!!opts?.requireBoq || !!opts?.boqLineId);
    if (opts?.boqLineId) {
      selectBoqLine(opts.boqLineId);
      costForm.setFieldsValue({ clientBoqLineId: opts.boqLineId });
    }
    setCostAddOpen(true);
  };

  const openBoqCostAdd = (boqId: string) => openCostAdd({ boqLineId: boqId, requireBoq: true });

  const materialLines = useMemo(
    () => (costQ.data || []).filter((c: any) => c.resource_kind === 'MATERIAL'),
    [costQ.data]
  );
  const matLine = useMemo(
    () => materialLines.find((l: any) => l.id === matLineId) || null,
    [materialLines, matLineId]
  );
  const labourLines = useMemo(
    () => (costQ.data || []).filter((c: any) => c.resource_kind === 'LABOUR'),
    [costQ.data]
  );
  const equipLines = useMemo(
    () => (costQ.data || []).filter((c: any) => c.resource_kind === 'EQUIPMENT'),
    [costQ.data]
  );
  const labourLine = useMemo(
    () => labourLines.find((l: any) => l.id === labourLineId) || null,
    [labourLines, labourLineId]
  );
  const equipLine = useMemo(
    () => equipLines.find((l: any) => l.id === equipLineId) || null,
    [equipLines, equipLineId]
  );

  const invalidateCosting = () => {
    qc.invalidateQueries({ queryKey: ['epc-est-boq', estimateId, revisionId] });
    qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
    qc.invalidateQueries({ queryKey: ['epc-est-explorer', estimateId, revisionId] });
    qc.invalidateQueries({ queryKey: ['epc-est-subpkg', estimateId, revisionId] });
    qc.invalidateQueries({ queryKey: ['epc-est-subcov', estimateId, revisionId] });
    qc.invalidateQueries({ queryKey: ['epc-est-readiness', estimateId] });
    qc.invalidateQueries({ queryKey: ['epc-est-boq-coverage', estimateId, revisionId] });
    qc.invalidateQueries({ queryKey: ['epc-est-explorer', estimateId, revisionId] });
  };

  const invalidateWbs = () => {
    qc.invalidateQueries({ queryKey: ['epc-est-wbs', estimateId, revisionId] });
    qc.invalidateQueries({ queryKey: ['epc-est-readiness', estimateId] });
    qc.invalidateQueries({ queryKey: ['epc-est-diagnostics', estimateId] });
  };

  const createM = useMutation({
    mutationFn: (departmentId: string) =>
      epcEstimationService.create({ tenderId, title: tenderTitle, departmentId }),
    onSuccess: () => {
      message.success('EPC Estimate created');
      setCreateEstimateOpen(false);
      createEstimateForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-estimates', tenderId] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Create failed'),
  });

  const reviseM = useMutation({
    mutationFn: () => epcEstimationService.createRevision(estimateId!),
    onSuccess: (rev: any) => {
      message.success(`Created ${rev.revision_label}`);
      setSelectedRevisionId(rev.id);
      qc.invalidateQueries({ queryKey: ['epc-estimate-ws', estimateId] });
      invalidateCosting();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Revise failed'),
  });

  const boqM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createClientBoq(estimateId!, { revisionId, ...v }),
    onSuccess: () => {
      message.success('BOQ line added');
      boqForm.resetFields();
      invalidateCosting();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'BOQ failed'),
  });

  const clearBoqM = useMutation({
    mutationFn: () => epcEstimationService.clearClientBoq(estimateId!, revisionId!),
    onSuccess: (data: { deleted?: number }) => {
      message.success(`Cleared ${data?.deleted ?? 0} Client BOQ line(s)`);
      invalidateCosting();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Clear BOQ failed'),
  });

  const confirmClearBoq = () => {
    if (!estimateId || !revisionId) {
      message.warning('Select an estimate revision first');
      return;
    }
    const count = (boqQ.data || []).length;
    Modal.confirm({
      title: 'Clear Client BOQ?',
      content: `Soft-delete all ${count} Client BOQ line(s) on this revision so you can re-import. Linked cost lines are not deleted.`,
      okText: 'Clear BOQ',
      okButtonProps: { danger: true },
      onOk: () => clearBoqM.mutateAsync(),
    });
  };

  const bulkBoqPatchM = useMutation({
    mutationFn: (patches: Array<{
      lineId: string;
      expectedUpdatedAt?: string | null;
      quantity?: number;
      unitRate?: number;
      description?: string;
      notes?: string;
      uom?: string;
    }>) => epcEstimationService.bulkPatchClientBoq(estimateId!, revisionId!, patches),
    onSuccess: () => {
      invalidateCosting();
      qc.invalidateQueries({ queryKey: ['epc-est-diagnostics', estimateId] });
      // Refresh TEC / pricing from backend SoR
      qc.invalidateQueries({ queryKey: ['epc-est-total', estimateId, revisionId] });
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Batch save failed'),
  });

  const [plugOpen, setPlugOpen] = useState(false);

  const costM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createCostLine(estimateId!, { revisionId, ...v }),
    onSuccess: (created: any) => {
      costForm.resetFields();
      invalidateCosting();
      const kind = String(created?.resource_kind || costAddKindFilter || '');
      const lineId = created?.id as string | undefined;
      if (lineId && ['MATERIAL', 'LABOUR', 'EQUIPMENT'].includes(kind)) {
        const key = `est3-cost-added-${lineId}`;
        notification.success({
          key,
          message: 'Cost line added',
          description: 'Add build-up detail for this line.',
          btn: (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                if (kind === 'MATERIAL') setMatLineId(lineId);
                else if (kind === 'LABOUR') setLabourLineId(lineId);
                else if (kind === 'EQUIPMENT') setEquipLineId(lineId);
                notification.destroy(key);
              }}
            >
              Open build-up
            </Button>
          ),
          duration: 8,
        });
      } else {
        message.success('Cost line added');
      }
      setCostAddKindFilter(undefined);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Cost failed'),
  });

  const bulkAssignM = useMutation({
    mutationFn: (p: { lineIds: string[]; wbsNodeId?: string; cbsCodeId?: string }) =>
      epcEstimationService.bulkAssignCostLines(estimateId!, revisionId!, p),
    onSuccess: (data: { updated?: number; failed?: number }) => {
      const updated = data?.updated ?? 0;
      const failed = data?.failed ?? 0;
      if (failed > 0) {
        message.warning(`Bulk assign: ${updated} updated, ${failed} failed`);
      } else {
        message.success(`Bulk assign: ${updated} line(s) updated`);
      }
      invalidateCosting();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Bulk assign failed'),
  });

  const patchCostM = useMutation({
    mutationFn: (p: { lineId: string; patch: Record<string, unknown> }) =>
      epcEstimationService.patchCostLine(estimateId!, p.lineId, revisionId!, p.patch),
    onSuccess: () => {
      message.success('Cost line updated');
      invalidateCosting();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Update failed'),
  });

  const subPkgM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createSubcontractPackage(estimateId!, { revisionId, ...v }),
    onSuccess: () => {
      message.success('Subcontract package added');
      subPkgForm.resetFields();
      invalidateCosting();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Package failed'),
  });

  const crewCreateM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createCrewTemplate(estimateId!, {
        code: v.code,
        name: v.name,
        description: v.description,
        members: v.resourceClassId
          ? [{ resourceClassId: v.resourceClassId, headcount: v.headcount ?? 1 }]
          : [],
      }),
    onSuccess: () => {
      message.success('Crew template created');
      crewCreateForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-crews', estimateId] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Crew create failed'),
  });

  const prodCreateM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createProductivityNorm(estimateId!, v),
    onSuccess: () => {
      message.success('Productivity norm created');
      prodCreateForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-prod', estimateId] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Norm create failed'),
  });

  const labourRateM = useMutation({
    mutationFn: (v: Record<string, unknown>) => epcEstimationService.upsertLabourRate(estimateId!, v),
    onSuccess: () => {
      message.success('Labour rate saved');
      labourRateForm.resetFields();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Rate save failed'),
  });

  const submitM = useMutation({
    mutationFn: () => epcEstimationService.submitForApproval(estimateId!, revisionId!),
    onSuccess: () => {
      message.success('Submitted for approval');
      qc.invalidateQueries({ queryKey: ['epc-estimate-ws', estimateId] });
      qc.invalidateQueries({ queryKey: ['approval-summary'] });
      setApprovalDrawerOpen(true);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Submit failed'),
  });

  const revOptions = useMemo(
    () =>
      (wsQ.data?.revisions || []).map((r: any) => ({
        value: r.id,
        label: `${r.revision_label} · ${r.status}`,
      })),
    [wsQ.data?.revisions]
  );

  const wave5Ctx =
    estimateId && revisionId
      ? { estimateId, revisionId, readOnly: !!readOnly }
      : null;
  // Must run unconditionally (before early returns) — queries are gated via enabled.
  const { totalQ, indirectQ } = useWave5Queries(wave5Ctx);

  const proposalsQ = useQuery({
    queryKey: ['epc-proposals', tenderId],
    queryFn: () => epcCommercialService.listProposals(tenderId),
    enabled: !!tenderId,
    staleTime: 30_000,
  });

  const primaryProposal = useMemo(() => {
    const rows = proposalsQ.data || [];
    return rows[0] || null;
  }, [proposalsQ.data]);

  const proposalBasisStatus = useMemo(() => {
    if (!primaryProposal) return null as 'CERTIFIED' | 'STALE' | 'NOT_RECORDED' | null;
    const meta = (primaryProposal.meta_json || primaryProposal.metaJson || {}) as Record<
      string,
      unknown
    >;
    const pin = (meta.est3_pricing_pin || meta.est3PricingPin || null) as Record<
      string,
      unknown
    > | null;
    if (!pin) return 'NOT_RECORDED';
    const pinRev = String(pin.estimate_revision_id || pin.estimateRevisionId || '');
    if (pinRev && revisionId && pinRev !== revisionId && String(current?.status) === 'Approved') {
      return 'STALE';
    }
    return 'CERTIFIED';
  }, [primaryProposal, revisionId, current?.status]);

  const pricingReady = useMemo(() => {
    const sections = readinessQ.data?.sections as Record<string, any> | undefined;
    const cp = sections?.commercial_pricing || sections?.pricing;
    if (!cp) {
      return totalQ.data?.final_bid_price != null ? true : null;
    }
    const st = String(cp.status || cp.readiness || '').toUpperCase();
    return st === 'READY' || st === 'COMPLETE' || st === 'OK';
  }, [readinessQ.data, totalQ.data?.final_bid_price]);

  if (runtimeQ.data?.disabled) {
    return <Alert type="warning" showIcon message="EPC Estimation is disabled for this environment." />;
  }

  if (!estimateId) {
    return (
      <Card>
        <Space direction="vertical">
          <Text type="secondary">No EST3 estimate for this tender yet.</Text>
          <Button type="primary" onClick={() => setCreateEstimateOpen(true)}>
            Create EPC Estimate
          </Button>
        </Space>
        <Modal
          title="Create EPC Estimate"
          open={createEstimateOpen}
          onCancel={() => {
            setCreateEstimateOpen(false);
            createEstimateForm.resetFields();
          }}
          onOk={() => createEstimateForm.submit()}
          confirmLoading={createM.isPending}
          destroyOnClose
        >
          <Form
            form={createEstimateForm}
            layout="vertical"
            onFinish={(v) => createM.mutate(String(v.department_id))}
          >
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: 'Department is required' }]}
            >
              <DepartmentSelect />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    );
  }

  const est = wsQ.data?.estimate;
  const currency = est?.base_currency || current?.currency;
  const approved = String(current?.status || '') === 'Approved';
  const submissionBasis = readinessQ.data?.sections?.submission_basis?.status;
  const budgetGenerated = !!readinessQ.data?.sections?.budget_handoff?.original_budget_id
    || readinessQ.data?.sections?.budget_handoff?.status === 'GENERATED'
    || readinessQ.data?.budget_handoff === 'GENERATED';

  const wbsPanel =
    estimateId && revisionId ? (
      <EpcEstimateWbsWorkbench
        estimateId={estimateId}
        revisionId={revisionId}
        readOnly={!!readOnly}
        nodes={wbsQ.data || []}
        loading={wbsQ.isLoading}
        onChanged={invalidateWbs}
      />
    ) : null;

  const cbsPanel = (
    <Card title="Company cost codes" size="small">
      <Table
        size="small"
        rowKey="id"
        loading={cbsQ.isLoading}
        dataSource={cbsQ.data || []}
        pagination={false}
        columns={[
          { title: 'Code', dataIndex: 'code', width: 100 },
          { title: 'Name', dataIndex: 'name' },
        ]}
      />
    </Card>
  );

  const approvalPanel = revisionId ? (
    <Card size="small" title="Estimate approval">
      <ApprovalPanel
        entityType={ENTITY_TYPE_EPC_ESTIMATE}
        entityId={revisionId}
        extraInvalidateQueryKeys={[
          ['epc-estimate-ws', estimateId],
          ['epc-est-readiness', estimateId],
          ['epc-est-total', estimateId],
        ]}
      />
    </Card>
  ) : null;

  const filterBoqLineId =
    coverageFilter === 'unlinked' ? null : selectedBoqLineId;


  const costWorkbenchCommon =
    estimateId && revisionId
      ? {
          estimateId,
          revisionId,
          readOnly: !!readOnly,
          lines: costQ.data || [],
          wbsOptions: wbsQ.data || [],
          cbsOptions: cbsQ.data || [],
          boqOptions: boqQ.data || [],
          loading: costQ.isLoading,
          costForm,
          onAdd: (v: Record<string, unknown>) => costM.mutate(v),
          addPending: costM.isPending,
          onOpenMaterials: setMatLineId,
          onOpenLabour: setLabourLineId,
          onOpenEquipment: setEquipLineId,
          onBulkAssign: async (lineIds, patch) => {
            await bulkAssignM.mutateAsync({ lineIds, ...patch });
          },
          bulkAssignPending: bulkAssignM.isPending,
          useDrawerAdd: true,
          defaultBoqLineId: selectedBoqLineId,
          onSetupWbs: () => setWbsImportOpen(true),
          filterBoqLineId:
            coverageFilter === 'unlinked'
              ? '__unlinked__'
              : filterBoqLineId,
        }
      : null;

  const panels: Partial<Record<Est3NavLeafKey, React.ReactNode>> = {
    health:
      estimateId && revisionId ? (
        <EpcEstimateOverviewHealth
          estimateId={estimateId}
          revisionId={revisionId}
          readiness={readinessQ.data}
          total={totalQ.data}
          wave6Ctx={wave5Ctx}
        />
      ) : null,
    boq:
      estimateId && revisionId ? (
        <EpcEstimateBoqLineWorkspace
          estimateId={estimateId}
          revisionId={revisionId}
          readOnly={!!readOnly}
          boqLines={boqQ.data || []}
          costLines={costQ.data || []}
          boqLoading={boqQ.isLoading}
          boqForm={boqForm}
          onAddBoq={(v) => boqM.mutate(v)}
          boqAddPending={boqM.isPending}
          onBoqImported={invalidateCosting}
          onClearBoq={confirmClearBoq}
          boqRollups={boqRollups}
          selectedBoqLineId={selectedBoqLineId}
          onSelectBoqLine={selectBoqLine}
          onAddCostForBoq={openBoqCostAdd}
          onOpenCostDetail={setCostDetailLine}
          onOpenMaterials={setMatLineId}
          density={density}
          immersive={immersive}
          useEstimatingWorkbench={immersive}
          wbsNodes={wbsQ.data || []}
          healthIssues={diagFindings as HealthIssue[]}
          onBatchSaveBoq={async (patches) => {
            const res = await bulkBoqPatchM.mutateAsync(patches);
            return {
              updated: Number(res?.updated || 0),
              failed: Number(res?.failed || 0),
              results: res?.results || [],
            };
          }}
          batchSavePending={bulkBoqPatchM.isPending}
          jumpToBoqId={null}
          onOpenBuildUp={(line) => {
            const kind = String(line.resource_kind || '');
            const id = String(line.id || '');
            if (!id) return;
            if (kind === 'MATERIAL') setMatLineId(id);
            else if (kind === 'LABOUR') setLabourLineId(id);
            else if (kind === 'EQUIPMENT') setEquipLineId(id);
            else setCostDetailLine(line);
          }}
          onWorkbenchMutated={() => {
            invalidateCosting();
            qc.invalidateQueries({ queryKey: ['epc-est-diagnostics', estimateId] });
            qc.invalidateQueries({ queryKey: ['epc-est-boq-detail', estimateId, revisionId] });
            qc.invalidateQueries({ queryKey: ['epc-est-total', estimateId, revisionId] });
          }}
          onOpenBoqLine={(id) => {
            selectBoqLine(id);
            setBoqLineDrawerId(id);
          }}
          coverageChipFilter={boqChipFilter}
          onCoverageChipFilterChange={setBoqChipFilter}
          importOpen={boqImportOpen}
          onImportOpenChange={setBoqImportOpen}
          addOpen={boqAddOpen}
          onAddOpenChange={setBoqAddOpen}
        />
      ) : null,
    wbs: wbsPanel,
    cbs: cbsPanel,
    cost: costWorkbenchCommon ? (
      <EpcEstimateDirectCostWorkbench {...costWorkbenchCommon} title="Direct cost summary" />
    ) : null,
    materials:
      estimateId && revisionId && costWorkbenchCommon ? (
        <EpcEstimateMaterialsWorkbench
          estimateId={estimateId}
          revisionId={revisionId}
          readOnly={!!readOnly}
          materialLines={materialLines}
          loading={costQ.isLoading}
          onOpenAdd={() => openCostAdd({ kind: 'MATERIAL' })}
          onCostingChanged={invalidateCosting}
        />
      ) : null,
    labour:
      estimateId && revisionId ? (
        <EpcEstimateLabourWorkbench
          estimateId={estimateId}
          revisionId={revisionId}
          readOnly={!!readOnly}
          labourLines={labourLines}
          loading={costQ.isLoading}
          onOpenAdd={() => openCostAdd({ kind: 'LABOUR' })}
          labourClasses={labourClassesQ.data || []}
          crews={crewQ.data || []}
          productivityNorms={prodQ.data || []}
          onCostingChanged={invalidateCosting}
        />
      ) : null,
    equipment:
      estimateId && revisionId ? (
        <EpcEstimateEquipmentWorkbench
          estimateId={estimateId}
          revisionId={revisionId}
          readOnly={!!readOnly}
          equipmentLines={equipLines}
          loading={costQ.isLoading}
          onOpenAdd={() => openCostAdd({ kind: 'EQUIPMENT' })}
          equipmentClasses={equipClassesQ.data || []}
          onCostingChanged={invalidateCosting}
        />
      ) : null,
    subcontract:
      estimateId && revisionId ? (
        <EpcEstimateSubcontractWorkbench
          estimateId={estimateId}
          revisionId={revisionId}
          readOnly={!!readOnly}
          packages={subPkgQ.data || []}
          coverage={subCovQ.data}
          loading={subPkgQ.isLoading}
          subPkgForm={subPkgForm}
          onAddPackage={(v) => subPkgM.mutate(v)}
          addPending={subPkgM.isPending}
          wbsOptions={wbsQ.data || []}
          cbsOptions={cbsQ.data || []}
          onCostingChanged={invalidateCosting}
        />
      ) : null,
    explorer:
      estimateId && revisionId ? (
        <EpcEstimateCostExplorerPanel
          estimateId={estimateId}
          revisionId={revisionId}
          onSelectBoqBucket={(id) => selectBoqLine(id)}
        />
      ) : null,
    duration:
      wave5Ctx ? (
        <EpcEstimateProjectCostLeaf ctx={wave5Ctx} leaf="duration">
          <Wave5DurationPanel ctx={wave5Ctx} />
        </EpcEstimateProjectCostLeaf>
      ) : null,
    indirects:
      wave5Ctx ? (
        <EpcEstimateProjectCostLeaf ctx={wave5Ctx} leaf="indirects">
          <Wave5IndirectsPanel ctx={wave5Ctx} />
        </EpcEstimateProjectCostLeaf>
      ) : null,
    time_related: wave5Ctx ? <EpcEstimateTimeRelatedWorkbench ctx={wave5Ctx} /> : null,
    risks:
      wave5Ctx ? (
        <EpcEstimateProjectCostLeaf ctx={wave5Ctx} leaf="risks">
          <Wave5RisksPanel ctx={wave5Ctx} />
        </EpcEstimateProjectCostLeaf>
      ) : null,
    contingency:
      wave5Ctx ? (
        <EpcEstimateProjectCostLeaf ctx={wave5Ctx} leaf="contingency">
          <Wave5ContingencyPanel ctx={wave5Ctx} />
        </EpcEstimateProjectCostLeaf>
      ) : null,
    escalation:
      wave5Ctx ? (
        <EpcEstimateProjectCostLeaf ctx={wave5Ctx} leaf="escalation">
          <Wave5EscalationPanel ctx={wave5Ctx} />
        </EpcEstimateProjectCostLeaf>
      ) : null,
    fx:
      wave5Ctx ? (
        <EpcEstimateProjectCostLeaf ctx={wave5Ctx} leaf="fx">
          <Wave5FxPanel ctx={wave5Ctx} />
        </EpcEstimateProjectCostLeaf>
      ) : null,
    pricing_strategy:
      wave5Ctx ? (
        <EpcEstimateCommercialLeaf ctx={wave5Ctx} leaf="pricing_strategy">
          <StrategyPanel ctx={wave5Ctx} />
        </EpcEstimateCommercialLeaf>
      ) : null,
    client_pricing:
      wave5Ctx ? (
        <EpcEstimateCommercialLeaf ctx={wave5Ctx} leaf="client_pricing">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <EpcEstimateCommercialBoqTable
              buckets={boqExplorerQ.data?.buckets || []}
              loading={boqExplorerQ.isLoading}
              onSelectBoq={(id) => selectBoqLine(id)}
            />
            <ClientPricingPanel ctx={wave5Ctx} />
          </Space>
        </EpcEstimateCommercialLeaf>
      ) : null,
    profitability:
      wave5Ctx ? (
        <EpcEstimateCommercialLeaf ctx={wave5Ctx} leaf="profitability">
          <ProfitabilityPanel ctx={wave5Ctx} />
        </EpcEstimateCommercialLeaf>
      ) : null,
    sensitivity:
      wave5Ctx ? (
        <EpcEstimateCommercialLeaf ctx={wave5Ctx} leaf="sensitivity">
          <SensitivityPanel ctx={wave5Ctx} />
        </EpcEstimateCommercialLeaf>
      ) : null,
    bid_reconciliation:
      wave5Ctx ? (
        <EpcEstimateCommercialLeaf ctx={wave5Ctx} leaf="bid_reconciliation">
          <BidReconciliationPanel ctx={wave5Ctx} />
        </EpcEstimateCommercialLeaf>
      ) : null,
    documents:
      estimateId && revisionId ? (
        <EpcEstimateEvidencePanel
          estimateId={estimateId}
          revisionId={revisionId}
          readOnly={!!readOnly}
        />
      ) : null,
    readiness: estimateId ? <EpcEstimateGovernanceReadinessPanel estimateId={estimateId} /> : null,
    approval: approvalPanel,
    revisions:
      estimateId ? (
        <EpcEstimateRevisionsWorkbench
          estimateId={estimateId}
          revisionId={revisionId}
          revisions={wsQ.data?.revisions || []}
          onSelectRevision={setSelectedRevisionId}
        />
      ) : null,
    submission:
      estimateId && revisionId ? (
        <EpcEstimateGovernancePanel estimateId={estimateId} revisionId={revisionId} />
      ) : null,
    diagnostics:
      estimateId && revisionId ? (
        <EpcEstimateDiagnosticsWorkbench
          estimateId={estimateId}
          revisionId={revisionId}
          costLineCount={(costQ.data || []).length}
          boqLineCount={(boqQ.data || []).length}
        />
      ) : null,
    budget_handoff: (
      <EpcBudgetHandoffPanel estimateId={estimateId || undefined} revisionId={revisionId} />
    ),
  };

  const availableLeaves = (() => {
    const s = new Set<Est3NavLeafKey>();
    (Object.keys(panels) as Est3NavLeafKey[]).forEach((k) => {
      if (panels[k] != null) s.add(k);
    });
    return s;
  })();

  const tecValue =
    totalQ.data?.total_estimated_cost != null
      ? Number(totalQ.data.total_estimated_cost)
      : totalQ.data?.tec != null
        ? Number(totalQ.data.tec)
        : null;

  const immersiveBody =
    immersiveView === 'boq' ? (
      panels.boq
    ) : (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Button type="link" style={{ padding: 0 }} onClick={() => setImmersiveView('boq')}>
          ← Back to Client BOQ
        </Button>
        {panels[immersiveView]}
      </Space>
    );

  return (
    <WorkspaceLayout
      fullBleed={immersive}
      denseBody={immersive}
      header={
        immersive ? (
          <EpcEstimateCommandBar
            estimateNumber={est?.estimate_number}
            revisionLabel={current?.revision_label}
            status={current?.status}
            currency={currency}
            revOptions={revOptions}
            revisionId={revisionId}
            onRevisionChange={setSelectedRevisionId}
            tec={tecValue}
            coverage={boqCoverageQ.data}
            blockerCount={blockerCount}
            readOnly={!!readOnly}
            revisePending={reviseM.isPending}
            submitPending={submitM.isPending}
            bcrNav={bcrNav}
            onAddLine={() => setBoqAddOpen(true)}
            onImport={() => setBoqImportOpen(true)}
            onClearBoq={confirmClearBoq}
            clearBoqPending={clearBoqM.isPending}
            onPricing={() => setCommercialDrawer('pricing')}
            onIndirects={() => setCommercialDrawer('indirects')}
            onContingency={() => setCommercialDrawer('contingency')}
            onPrepareProposal={() => setPrepareProposalOpen(true)}
            onPrint={() => {
              if (!estimateId || !revisionId) {
                message.warning('Select an estimate revision first');
                return;
              }
              void openEpcEstimatePrint({
                estimateId,
                revisionId,
                tenderTitle: tenderTitle || undefined,
              });
            }}
            onApproval={() => {
              setApprovalDrawerOpen(true);
              setImmersiveView('approval');
              setControlLeaf('approval');
            }}
            approvalPending={String(current?.status || '') === 'ApprovalPending'}
            onSections={() => setSectionsOpen(true)}
            onSummary={() => setIntelligenceOpen(true)}
            onPlugLibrary={() => {
              if (!selectedBoqLineId) {
                message.info('Select a BOQ line first, then plug a WBS Library package');
                return;
              }
              setPlugOpen(true);
            }}
            sellPrice={
              totalQ.data?.final_bid_price != null ? Number(totalQ.data.final_bid_price) : null
            }
            marginPct={
              totalQ.data?.margin_actual != null ? Number(totalQ.data.margin_actual) : null
            }
            pricingReady={pricingReady}
            indirectsCount={(indirectQ.data || []).length}
            proposalBasisStatus={proposalBasisStatus}
            onRevise={() => reviseM.mutate()}
            onSubmit={() => submitM.mutate()}
            onBoqWithoutCostClick={() => {
              setBoqChipFilter('without_cost');
              setImmersiveView('boq');
            }}
            onDiagnostics={() => {
              setImmersiveView('diagnostics');
              setControlLeaf('diagnostics');
            }}
          />
        ) : focusMode ? (
          <EpcEstimateCommandBar
            estimateNumber={est?.estimate_number}
            revisionLabel={current?.revision_label}
            status={current?.status}
            currency={currency}
            revOptions={revOptions}
            revisionId={revisionId}
            onRevisionChange={setSelectedRevisionId}
            tec={tecValue}
            coverage={boqCoverageQ.data}
            blockerCount={blockerCount}
            readOnly={!!readOnly}
            revisePending={reviseM.isPending}
            submitPending={submitM.isPending}
            onPricing={() => setCommercialDrawer('pricing')}
            onIndirects={() => setCommercialDrawer('indirects')}
            onContingency={() => setCommercialDrawer('contingency')}
            onPrepareProposal={() => setPrepareProposalOpen(true)}
            onPrint={() => {
              if (!estimateId || !revisionId) {
                message.warning('Select an estimate revision first');
                return;
              }
              void openEpcEstimatePrint({
                estimateId,
                revisionId,
                tenderTitle: tenderTitle || undefined,
              });
            }}
            onApproval={() => {
              setApprovalDrawerOpen(true);
              setImmersiveView('approval');
              setControlLeaf('approval');
            }}
            approvalPending={String(current?.status || '') === 'ApprovalPending'}
            onSummary={() => setIntelligenceOpen(true)}
            sellPrice={
              totalQ.data?.final_bid_price != null ? Number(totalQ.data.final_bid_price) : null
            }
            marginPct={
              totalQ.data?.margin_actual != null ? Number(totalQ.data.margin_actual) : null
            }
            pricingReady={pricingReady}
            indirectsCount={(indirectQ.data || []).length}
            proposalBasisStatus={proposalBasisStatus}
            onRevise={() => reviseM.mutate()}
            onSubmit={() => submitM.mutate()}
          />
        ) : (
        <EnterprisePageHeader
          documentLabel="EPC Estimate"
          documentNumber={
            est?.estimate_number
              ? `${est.estimate_number} · ${current?.revision_label || '—'}`
              : undefined
          }
          statusTag={
            <Space wrap size={4}>
              <Tag>{current?.status || '—'}</Tag>
              {current?.department_id ? (
                <Tag color="geekblue">Dept set</Tag>
              ) : null}
              <Tag color="blue">{currency}</Tag>
            </Space>
          }
          subtitle={
            <Text type="secondary">
              {est?.title}
              {tenderTitle ? ` · Tender ${tenderTitle}` : ''}
            </Text>
          }
          facts={
            <Select
              style={{ minWidth: 200 }}
              options={revOptions}
              value={revisionId}
              onChange={setSelectedRevisionId}
              placeholder="Revision"
            />
          }
          primaryActions={
            <Space wrap>
              <Button onClick={() => reviseM.mutate()} loading={reviseM.isPending}>
                Create Revision
              </Button>
              {!readOnly && (
                <Button type="primary" onClick={() => submitM.mutate()} loading={submitM.isPending}>
                  Submit for Approval
                </Button>
              )}
            </Space>
          }
        />
        )
      }
    >
      {immersive ? (
        immersiveBody
      ) : (
      <Row gutter={[12, 12]}>
        <Col xs={24} xl={focusMode ? 24 : 18}>
          {!focusMode ? (
            <>
          <EpcEstimateWorkflowStrip
            revisionStatus={current?.status}
            costModel={readinessQ.data?.cost_model}
            pricing={readinessQ.data?.commercial_pricing}
            approved={approved}
          />
          <EpcEstimateCommercialHero
            currency={currency}
            revisionLabel={current?.revision_label}
            tec={tecValue}
            finalBid={
              totalQ.data?.final_bid_price != null ? Number(totalQ.data.final_bid_price) : null
            }
            marginPct={
              totalQ.data?.margin_actual != null ? Number(totalQ.data.margin_actual) : null
            }
            costModel={readinessQ.data?.cost_model}
            pricing={readinessQ.data?.commercial_pricing}
            boqCoveragePct={boqCoverageQ.data?.pct_costs_linked}
          />
          <EpcEstimateAttentionPanel
            readiness={readinessQ.data}
            diagnostics={diagFindings}
            boqCoverage={boqCoverageQ.data}
          />
            </>
          ) : null}
          <EpcEstimateControlCenter
            focusMode={focusMode}
            defaultLeaf={EST3_DEFAULT_LEAF}
            requestedLeaf={controlLeaf}
            statusStrip={
              <>
                {!focusMode ? (
                <EpcEstimateStatusStrip
                  currency={currency}
                  revisionStatus={current?.status}
                  costModel={readinessQ.data?.cost_model}
                  pricing={readinessQ.data?.commercial_pricing}
                  approved={approved}
                  submissionBasis={submissionBasis}
                  pinned={
                    readinessQ.data?.sections?.submission_basis?.status === 'CERTIFIED' ||
                    !!readinessQ.data?.sections?.award_basis?.pinned
                  }
                  originalBudgetGenerated={!!budgetGenerated}
                  tec={tecValue}
                />
                ) : null}
                {estimateId && revisionId && !focusMode ? (
                  <EpcEstimateBoqCoverageStrip
                    estimateId={estimateId}
                    revisionId={revisionId}
                    currency={currency}
                    onFilterUnlinked={() => setCoverageFilter('unlinked')}
                    onFilterBoqWithoutCost={() => selectBoqLine(null)}
                  />
                ) : null}
              </>
            }
            panels={panels}
          />
        </Col>
        {!focusMode ? (
        <Col xs={24} xl={6}>
          <EpcEstimateIntelligenceRail
            estimateNumber={est?.estimate_number}
            revisionLabel={current?.revision_label}
            tenderTitle={tenderTitle}
            revisionCount={(wsQ.data?.revisions || []).length}
            boqLineCount={(boqQ.data || []).length}
            costLineCount={(costQ.data || []).length}
          />
        </Col>
        ) : null}
      </Row>
      )}

      <EpcEstimateSectionsDrawer
        open={sectionsOpen}
        onClose={() => setSectionsOpen(false)}
        availableLeaves={availableLeaves}
        onSelectLeaf={(leaf) => {
          setImmersiveView(leaf);
          setControlLeaf(leaf);
        }}
      />

      <Drawer
        title={
          commercialDrawer === 'pricing'
            ? 'Pricing Strategy'
            : commercialDrawer === 'indirects'
              ? 'Indirect Costs'
              : commercialDrawer === 'contingency'
                ? 'Contingency'
                : commercialDrawer === 'duration'
                  ? 'Duration Assumptions'
                  : 'Commercial'
        }
        open={!!commercialDrawer && !!wave5Ctx}
        onClose={() => setCommercialDrawer(null)}
        width={Math.min(
          commercialDrawer === 'indirects' ? 960 : 720,
          typeof window !== 'undefined' ? window.innerWidth - 24 : 960
        )}
        styles={{ body: { paddingBottom: 24 } }}
        destroyOnClose={false}
      >
        {wave5Ctx && commercialDrawer === 'pricing' ? <StrategyPanel ctx={wave5Ctx} /> : null}
        {wave5Ctx && commercialDrawer === 'indirects' ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Button type="link" style={{ padding: 0 }} onClick={() => setCommercialDrawer('duration')}>
              Edit duration assumptions (drives duration-based indirects)
            </Button>
            <Wave5IndirectsPanel
              ctx={wave5Ctx}
              onOpenDuration={() => setCommercialDrawer('duration')}
            />
          </Space>
        ) : null}
        {wave5Ctx && commercialDrawer === 'contingency' ? (
          <Wave5ContingencyPanel ctx={wave5Ctx} />
        ) : null}
        {wave5Ctx && commercialDrawer === 'duration' ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Button type="link" style={{ padding: 0 }} onClick={() => setCommercialDrawer('indirects')}>
              ← Back to Indirects
            </Button>
            <Wave5DurationPanel ctx={wave5Ctx} />
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        title="Estimate approval"
        open={approvalDrawerOpen && !!revisionId}
        onClose={() => setApprovalDrawerOpen(false)}
        width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 24 : 560)}
        destroyOnClose={false}
      >
        {revisionId ? (
          <ApprovalPanel
            entityType={ENTITY_TYPE_EPC_ESTIMATE}
            entityId={revisionId}
            extraInvalidateQueryKeys={[
              ['epc-estimate-ws', estimateId],
              ['epc-est-readiness', estimateId],
              ['epc-est-total', estimateId],
            ]}
          />
        ) : (
          <Alert type="info" showIcon message="Select a revision to view approval." />
        )}
      </Drawer>

      {estimateId ? (
        <EpcPrepareProposalFromEstimateModal
          open={prepareProposalOpen}
          onClose={() => setPrepareProposalOpen(false)}
          tenderId={tenderId}
          estimateId={estimateId}
          currency={String(currency || 'AED')}
          revisions={(wsQ.data?.revisions || []) as Array<Record<string, unknown>>}
          existingProposalId={primaryProposal ? String(primaryProposal.id) : null}
          onCertified={(proposalId) => {
            setPrepareProposalOpen(false);
            qc.invalidateQueries({ queryKey: ['epc-proposals', tenderId] });
            qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
            if (bcrNav?.onOpenProposal) {
              bcrNav.onOpenProposal();
            } else if (bcrNav?.onClassicWorkspace) {
              bcrNav.onClassicWorkspace();
            }
            message.success(`Proposal ${proposalId.slice(0, 8)}… certified from estimate`);
          }}
        />
      ) : null}

      {estimateId && revisionId ? (
        <EpcEstimateBoqLineDrawer
          open={!!boqLineDrawerId}
          onClose={() => setBoqLineDrawerId(null)}
          estimateId={estimateId}
          revisionId={revisionId}
          lineId={boqLineDrawerId}
          readOnly={!!readOnly}
          onAddCostLine={openBoqCostAdd}
          onOpenCostDetail={setCostDetailLine}
          onOpenMaterials={setMatLineId}
        />
      ) : null}

      <Drawer title="Estimate summary" open={intelligenceOpen} onClose={() => setIntelligenceOpen(false)} width={320}>
        <EpcEstimateIntelligenceRail
          estimateNumber={est?.estimate_number}
          revisionLabel={current?.revision_label}
          tenderTitle={tenderTitle}
          revisionCount={(wsQ.data?.revisions || []).length}
          boqLineCount={(boqQ.data || []).length}
          costLineCount={(costQ.data || []).length}
        />
      </Drawer>

      {costWorkbenchCommon ? (
        <EpcEstimateCostLineAddDrawer
          open={costAddOpen}
          onClose={() => {
            setCostAddOpen(false);
            setCostAddKindFilter(undefined);
            setCostAddRequireBoq(false);
          }}
          form={costForm}
          readOnly={!!readOnly}
          wbsOptions={wbsQ.data || []}
          cbsOptions={cbsQ.data || []}
          boqOptions={boqQ.data || []}
          costLines={costQ.data || []}
          kindFilter={costAddKindFilter}
          requireBoq={costAddRequireBoq}
          onSetupWbs={() => setWbsImportOpen(true)}
          onSubmit={(v) => costM.mutate(v)}
          pending={costM.isPending}
          defaultBoqLineId={selectedBoqLineId}
        />
      ) : null}

      {estimateId && revisionId ? (
        <EpcEstimateWbsImportWizard
          open={wbsImportOpen}
          onClose={() => setWbsImportOpen(false)}
          estimateId={estimateId}
          revisionId={revisionId}
          readOnly={!!readOnly}
          onImported={() => {
            invalidateWbs();
            setWbsImportOpen(false);
          }}
        />
      ) : null}

      {costDetailLine && costWorkbenchCommon ? (
        <EpcEstimateCostLineDrawer
          line={costDetailLine}
          open={!!costDetailLine}
          onClose={() => setCostDetailLine(null)}
          readOnly={!!readOnly}
          boqOptions={boqQ.data || []}
          wbsOptions={wbsQ.data || []}
          cbsOptions={cbsQ.data || []}
          wbsLabel={wbsLabelById(wbsQ.data || [], String(costDetailLine.wbs_node_id || ''))}
          cbsLabel={cbsLabelById(cbsQ.data || [], String(costDetailLine.cbs_code_id || ''))}
          onPatch={async (lineId, patch) => {
            await patchCostM.mutateAsync({ lineId, patch });
          }}
          patchPending={patchCostM.isPending}
          onOpenMaterials={setMatLineId}
          onOpenLabour={setLabourLineId}
          onOpenEquipment={setEquipLineId}
        />
      ) : null}

      <Drawer
        title="Material components (landed build-up)"
        open={!!matLineId}
        onClose={() => setMatLineId(null)}
        width={640}
      >
        {estimateId && revisionId && matLineId ? (
          <EpcEstimateMaterialBuildUpPanel
            estimateId={estimateId}
            revisionId={revisionId}
            costLineId={matLineId}
            costLine={matLine}
            readOnly={readOnly}
            onChanged={invalidateCosting}
            compact
          />
        ) : null}
      </Drawer>

      <Drawer
        title="Labour crew / rates"
        open={!!labourLineId}
        onClose={() => setLabourLineId(null)}
        width={640}
      >
        {estimateId && revisionId && labourLineId ? (
          <EpcEstimateLabourBuildUpPanel
            estimateId={estimateId}
            revisionId={revisionId}
            costLineId={labourLineId}
            costLine={labourLine}
            readOnly={readOnly}
            onChanged={invalidateCosting}
            compact
          />
        ) : null}
      </Drawer>

      <Drawer
        title="Equipment / plant build-up"
        open={!!equipLineId}
        onClose={() => setEquipLineId(null)}
        width={640}
      >
        {estimateId && revisionId && equipLineId ? (
          <EpcEstimateEquipmentBuildUpPanel
            estimateId={estimateId}
            revisionId={revisionId}
            costLineId={equipLineId}
            costLine={equipLine}
            readOnly={readOnly}
            onChanged={invalidateCosting}
            compact
          />
        ) : null}
      </Drawer>

      {estimateId && revisionId && selectedBoqLineId ? (
        <EpcWbsLibraryPlugModal
          open={plugOpen}
          onClose={() => setPlugOpen(false)}
          estimateId={estimateId}
          revisionId={revisionId}
          clientBoqLineId={selectedBoqLineId}
        />
      ) : null}
    </WorkspaceLayout>
  );
};

export default EpcEstimateWorkspaceShell;
